import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Send, CheckCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  'General Inquiry',
  'Appointment Query',
  'Report Upload Issue',
  'Technical Support',
  'Other',
];

const COUNTRY_CODES = [
  { code: '+91', label: 'IN +91' },
  { code: '+1', label: 'US +1' },
  { code: '+44', label: 'UK +44' },
  { code: '+971', label: 'AE +971' },
  { code: '+65', label: 'SG +65' },
  { code: '+61', label: 'AU +61' },
];

const ContactForm = () => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    mobile: '',
    country_code: '+91',
    subject: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) e.full_name = 'Full name is required (min 2 characters)';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!/^[0-9]{7,15}$/.test(form.mobile.trim())) e.mobile = 'Enter a valid mobile number (digits only)';
    if (!form.subject) e.subject = 'Please select a subject';
    if (!form.message.trim() || form.message.trim().length < 10) e.message = 'Message must be at least 10 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          mobile: form.mobile.trim(),
          country_code: form.country_code,
          subject: form.subject,
          message: form.message.trim(),
          honeypot: form.honeypot,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
      } else {
        setErrors({ submit: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch {
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="border-0 shadow-lg" data-testid="contact-form-success">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-slate-50">Message Sent!</h3>
          <p className="text-gray-600 dark:text-slate-300 text-sm leading-relaxed max-w-sm mx-auto">
            Thank you for reaching out. We'll get back to you shortly.
            A confirmation email has been sent to your inbox.
          </p>
          <Button
            variant="outline"
            onClick={() => { setSubmitted(false); setForm({ full_name: '', email: '', mobile: '', country_code: '+91', subject: '', message: '', honeypot: '' }); }}
            className="mt-2"
            data-testid="send-another-btn"
          >
            Send Another Message
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg" data-testid="contact-form-card">
      <CardContent className="p-6 sm:p-8">
        <h3 className="text-xl font-bold text-gray-900 dark:text-slate-50 mb-1">
          Send Us a Message
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">We'll respond within 24 hours</p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
          {/* Honeypot — hidden from humans */}
          <div className="absolute -left-[9999px]" aria-hidden="true" tabIndex={-1}>
            <input
              type="text"
              name="website"
              value={form.honeypot}
              onChange={(e) => handleChange('honeypot', e.target.value)}
              autoComplete="off"
              tabIndex={-1}
            />
          </div>

          {/* Full Name */}
          <div>
            <Label htmlFor="full_name" className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="full_name"
              placeholder="Enter your full name"
              value={form.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              className={`mt-1 ${errors.full_name ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
              data-testid="contact-name-input"
            />
            {errors.full_name && <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`mt-1 ${errors.email ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
              data-testid="contact-email-input"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          {/* Mobile */}
          <div>
            <Label htmlFor="mobile" className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Mobile Number <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2 mt-1">
              <select
                value={form.country_code}
                onChange={(e) => handleChange('country_code', e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-2 text-sm w-24 flex-shrink-0 text-foreground"
                data-testid="contact-country-code"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <Input
                id="mobile"
                type="tel"
                placeholder="9876543210"
                value={form.mobile}
                onChange={(e) => handleChange('mobile', e.target.value.replace(/\D/g, ''))}
                className={`flex-1 ${errors.mobile ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                data-testid="contact-mobile-input"
              />
            </div>
            {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject" className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Subject <span className="text-red-500">*</span>
            </Label>
            <select
              id="subject"
              value={form.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              className={`mt-1 flex h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground ${
                errors.subject ? 'border-red-400' : 'border-input'
              } ${!form.subject ? 'text-muted-foreground' : ''}`}
              data-testid="contact-subject-select"
            >
              <option value="" disabled>Select a subject</option>
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message" className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Message <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="message"
              rows={4}
              placeholder="Describe your inquiry in detail (min. 10 characters)"
              value={form.message}
              onChange={(e) => handleChange('message', e.target.value)}
              className={`mt-1 flex w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground resize-none ${
                errors.message ? 'border-red-400 focus-visible:ring-red-400' : 'border-input'
              } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
              data-testid="contact-message-input"
            />
            <div className="flex justify-between mt-1">
              {errors.message ? (
                <p className="text-xs text-red-500">{errors.message}</p>
              ) : <span />}
              <span className="text-xs text-gray-400 dark:text-slate-500">{form.message.length}/2000</span>
            </div>
          </div>

          {/* Submit error */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-300">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white py-5 text-sm font-semibold rounded-xl transition-all duration-200"
            data-testid="contact-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
